import { DeleteFilled, EditFilled } from '@ant-design/icons';
import { Button, Card, Col, Image, Row, Space, Tag } from 'antd';
import { clsx } from 'clsx';
import * as eva from 'eva-icons';
import { map } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { CloseIcon } from '../../assets/svg';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import {
  BREAKPOINTS,
  DATE_TIME_WITH_AT,
  GA_EVENT,
  GA_LABEL,
  REGISTER_LOG_STATUS,
  STAGE_STATUS,
  STAGE_STATUS_KEYS,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import { titleCase } from '../../common/utils';
import CommonPopover from '../../components/CommonPopover';
import EllipsisText from '../../components/EllipsisText';
import { GET_URL_BY_KEY } from '../profile/graphql/Queries';
import DeleteAdditionalCommentModal from './DeleteAdditionalCommentModal';
import RegisterCommentModal from './RegisterCommentModal';

const MAX_ADDITIONAL_COMMENTS = 10;

const RegisterAdditionalComment = ({
  data,
  refetch,
  commentData,
  setCommentData,
  setAdditionalComments,
  additionalComments,
}) => {
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);
  const [isAdditionalPoint, setIsAdditionalPoint] = useState(false);
  const [editingCommentIndex, setEditingCommentIndex] = useState(null);
  const [deletingCommentIndex, setDeletingCommentIndex] = useState(null);
  const [imageFileLists, setImageFileLists] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [previewImageKey, setPreviewImageKey] = useState('');
  const [viewCommentsOpen, setViewCommentsOpen] = useState(false);

  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [getImagesSignedUrls] = useLazyQueryWithCallbacks(GET_URL_BY_KEY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    // Fetch images for all comments
    additionalComments?.forEach((comment, index) => {
      const commentImages = map(comment?.assets, 'key');
      if (commentImages?.length > 0) {
        getImagesSignedUrls({
          variables: {
            data: { keys: commentImages },
          },
        }).then((res) => {
          if (res?.data?.getImagesSignedUrls) {
            setImageFileLists((prev) => ({
              ...prev,
              [index]: res?.data?.getImagesSignedUrls,
            }));
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalComments]);

  const getPhotos = (photos, imageId) => {
    return photos?.length > 0 ? (
      <Image.PreviewGroup
        preview={{
          visible: imageId === previewImageKey && isVisible,
          onVisibleChange: (visible) => {
            setIsVisible(visible);
            if (visible) {
              Event(GA_EVENT.OPENED_IMAGE_VIEWER, {
                label: GA_LABEL.OPENED_IMAGE_VIEWER,
                // eslint-disable-next-line no-undef
                pathname: window?.location?.href,
              });
            }
          },
        }}
      >
        <Space className="image-preview-wrapper">
          {React.Children.map(photos, (photo) => (
            <Image
              src={photo}
              alt="logo"
              height={isDesktopViewport ? '64px' : '32px'}
              width={isDesktopViewport ? '64px' : '32px'}
              onClick={() => setPreviewImageKey(imageId)}
            />
          ))}
          {photos?.length - 1 > 0 && (
            <div
              className="pointer extra-count"
              onClick={() => {
                setIsVisible(true);
                setPreviewImageKey(imageId);
              }}
            >{`+ ${photos?.length - 1}`}</div>
          )}
        </Space>
      </Image.PreviewGroup>
    ) : (
      '-'
    );
  };

  const handleAddComment = () => {
    setEditingCommentIndex(null);
    setShowCommentModal(true);
    setIsAdditionalPoint(true);
  };

  const handleEditComment = (index) => {
    setEditingCommentIndex(index);
    setShowCommentModal(true);
    setIsAdditionalPoint(true);
  };

  const handleDeleteComment = (index) => {
    setDeletingCommentIndex(index);
    setShowDeleteCommentModal(true);
  };

  const canAddMoreComments =
    additionalComments?.length < MAX_ADDITIONAL_COMMENTS;

  const stageStatusTags = (status) => {
    switch (status) {
      case STAGE_STATUS_KEYS.PASS:
        return <Tag className="active-tag">{titleCase(STAGE_STATUS.PASS)}</Tag>;
      case STAGE_STATUS_KEYS.APPROVED:
        return (
          <Tag className="active-tag">{titleCase(STAGE_STATUS.APPROVED)}</Tag>
        );
      case STAGE_STATUS_KEYS.ACCEPTED:
        return (
          <Tag className="active-tag">{titleCase(STAGE_STATUS.ACCEPTED)}</Tag>
        );
      case STAGE_STATUS_KEYS.APPROVAL_PENDING:
        return (
          <Tag className="yellow-tag">
            {titleCase(STAGE_STATUS.APPROVAL_PENDING)}
          </Tag>
        );
      case STAGE_STATUS_KEYS.REDO:
        return <Tag color="red">{titleCase(STAGE_STATUS.REDO)}</Tag>;
      case STAGE_STATUS_KEYS.REJECTED:
        return <Tag color="red">{titleCase(STAGE_STATUS.REJECTED)}</Tag>;

      default:
        return <Tag color="red">{titleCase(STAGE_STATUS.FAIL)}</Tag>;
    }
  };

  const renderAdditionalCommentsHistory = () => {
    const registerLogHistories = data?.registerLogHistories || [];

    return (
      <div className="additional-comments-history-content">
        {map(registerLogHistories, (historyItem, index) => {
          return (
            <React.Fragment key={index}>
              <Card className="approvers-card">
                <Row justify="space-between" gutter={18}>
                  <Col span={14}>
                    <EllipsisText
                      className="text-secondary mb-15 level-name"
                      text={historyItem?.levelName}
                    />
                    {historyItem?.actor?.name && (
                      <div>
                        By
                        <Tag className="ml-5 approver-name-tag">
                          <EllipsisText text={historyItem?.actor?.name} />
                        </Tag>
                      </div>
                    )}
                  </Col>
                  <Col span={10}>
                    <div className="mb-15">
                      {stageStatusTags(historyItem?.approvalStatus)}
                    </div>
                    <div className="font-size-12">
                      {moment(historyItem?.createdAt)
                        ?.tz(data?.register?.project?.timeZone)
                        ?.format(DATE_TIME_WITH_AT)}
                    </div>
                  </Col>
                </Row>

                {/* Render multiple additional comments */}
                {map(historyItem?.comments, (comment, commentIndex) => (
                  <div
                    key={commentIndex}
                    className="additional-comment-item mt-15"
                  >
                    {comment?.commentText && (
                      <Row>
                        <Col span={24}>
                          <div className="font-weight-medium mb-5">
                            Remark {commentIndex + 1}:
                          </div>
                          <div className="text-secondary">
                            {comment?.commentText}
                          </div>
                        </Col>
                      </Row>
                    )}
                    {comment?.assetUrls?.length > 0 && (
                      <Row className="mt-10">
                        <Col span={24}>
                          <div className="font-weight-medium mb-5">Images:</div>
                          <Image.PreviewGroup
                            preview={{
                              visible:
                                `history-${index}-comment-${commentIndex}` ===
                                  previewImageKey && isVisible,
                              onVisibleChange: (visible) => {
                                setIsVisible(visible);
                                if (visible) {
                                  Event(GA_EVENT.OPENED_IMAGE_VIEWER, {
                                    label: GA_LABEL.OPENED_IMAGE_VIEWER,
                                    // eslint-disable-next-line no-undef
                                    pathname: window?.location?.href,
                                  });
                                }
                              },
                            }}
                          >
                            <Space className="image-preview-wrapper">
                              <Image
                                src={comment?.assetUrls?.[0]}
                                alt="Additional comment"
                                height={isDesktopViewport ? '64px' : '32px'}
                                width={isDesktopViewport ? '64px' : '32px'}
                                onClick={() =>
                                  setPreviewImageKey(
                                    `history-${index}-comment-${commentIndex}`,
                                  )
                                }
                              />
                              {/* Hidden images for preview group */}
                              {comment?.assetUrls?.slice(1)?.map((photo) => (
                                <Image
                                  key={photo}
                                  src={photo}
                                  alt="Additional comment"
                                  className="d-none"
                                />
                              ))}
                              {comment?.assetUrls?.length > 1 && (
                                <div
                                  className="pointer extra-count"
                                  onClick={() => {
                                    setIsVisible(true);
                                    setPreviewImageKey(
                                      `history-${index}-comment-${commentIndex}`,
                                    );
                                  }}
                                >{`+ ${comment?.assetUrls?.length - 1}`}</div>
                              )}
                            </Space>
                          </Image.PreviewGroup>
                        </Col>
                      </Row>
                    )}
                  </div>
                ))}
              </Card>
              {index < registerLogHistories?.length - 1 && (
                <div className="approver-divider my-15" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {showCommentModal && (
        <RegisterCommentModal
          showModal={showCommentModal}
          setShowModal={setShowCommentModal}
          data={data}
          refetch={refetch}
          setCommentData={setCommentData}
          commentData={commentData}
          setAdditionalComments={setAdditionalComments}
          additionalComments={additionalComments}
          isAdditionalPoint={isAdditionalPoint}
          setIsAdditionalPoint={setIsAdditionalPoint}
          editingCommentIndex={editingCommentIndex}
          setEditingCommentIndex={setEditingCommentIndex}
        />
      )}
      <div className="approver-stage-details mb-20">
        <div>
          {additionalComments?.length > 0 &&
            data?.status === REGISTER_LOG_STATUS.APPROVAL_PENDING && (
              <div className="mb-24">
                <div className="d-flex justify-between align-center mb-16">
                  <h2 className="mb-0">Additional Comments</h2>
                </div>

                {map(additionalComments, (comment, index) => (
                  <Card key={index} className="mb-16">
                    <div>
                      <div className="d-flex justify-between">
                        <div>
                          <h3 className="mb-16">
                            Additional Comment {index + 1}
                          </h3>
                        </div>
                        <div className="action-button d-flex justify-end">
                          <Button
                            type="primary"
                            shape="round"
                            icon={<EditFilled />}
                            onClick={() => handleEditComment(index)}
                            className={`${
                              !isDesktopViewport &&
                              'width-percent-100 justify-center mb-24'
                            }`}
                          >
                            {isDesktopViewport && 'Edit'}
                          </Button>
                          <Button
                            type="primary"
                            shape="round"
                            danger
                            icon={<DeleteFilled />}
                            onClick={() => handleDeleteComment(index)}
                            className={`${
                              !isDesktopViewport &&
                              'width-percent-100 justify-center mb-24'
                            }`}
                          >
                            {isDesktopViewport && 'Delete'}
                          </Button>
                        </div>
                      </div>

                      {comment?.remark || comment?.assets?.length > 0 ? (
                        <Card className="comment-card">
                          {comment?.remark && (
                            <div>
                              <div className="d-flex align-center justify-between mb-5">
                                <EllipsisText
                                  className="additional-title"
                                  text={comment?.remark || '-'}
                                />
                              </div>
                            </div>
                          )}
                          {imageFileLists?.[index]?.length > 0 && (
                            <Row className="flex-vertical mt-24">
                              <h4>Additional Comment Images</h4>
                              <div
                                className={
                                  isDesktopViewport
                                    ? 'd-flex justify-between align-end'
                                    : 'd-flex flex-vertical'
                                }
                              >
                                <div className="mb-24 mt-5">
                                  {getPhotos(
                                    imageFileLists[index],
                                    `${comment?.remark}-${index}`,
                                  )}
                                </div>
                              </div>
                            </Row>
                          )}
                        </Card>
                      ) : (
                        ''
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
        </div>
        {data?.status === REGISTER_LOG_STATUS.APPROVAL_PENDING &&
          (additionalComments?.length === 0 || canAddMoreComments) && (
            <div className="d-flex align-center justify-between additional-comment-actions">
              <div className="additional-text" onClick={handleAddComment}>
                + Add Additional Comment
                {additionalComments?.length > 0 &&
                  ` (${additionalComments?.length}/${MAX_ADDITIONAL_COMMENTS})`}
              </div>
              {data?.registerLogHistories?.length > 0 && (
                <CommonPopover
                  classNames={{
                    root: 'approver-info additional-comments-popover',
                    container: 'approver-popover-container',
                  }}
                  onOpenChange={setViewCommentsOpen}
                  autoAdjustOverflow
                  getPopupContainer={() =>
                    // eslint-disable-next-line no-undef
                    document.querySelector('.approval-link')
                  }
                  title={
                    <div className="d-flex justify-between align-center">
                      <div>Previous Additional Comments</div>
                      <CloseIcon
                        height={24}
                        width={24}
                        className="pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewCommentsOpen(false);
                        }}
                      />
                    </div>
                  }
                  trigger="click"
                  placement={isDesktopViewport ? 'left' : 'top'}
                  destroyTooltipOnHide
                  open={viewCommentsOpen}
                  content={
                    <div onClick={(e) => e.stopPropagation()}>
                      {renderAdditionalCommentsHistory()}
                    </div>
                  }
                >
                  <Button
                    className={clsx(!isDesktopViewport && 'mb-10')}
                    shape="round"
                    type="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewCommentsOpen(true);
                    }}
                  >
                    View Additional Comments
                  </Button>
                </CommonPopover>
              )}
            </div>
          )}
      </div>
      {showDeleteCommentModal && deletingCommentIndex !== null && (
        <DeleteAdditionalCommentModal
          showModal={showDeleteCommentModal}
          setShowModal={setShowDeleteCommentModal}
          setAdditionalComments={setAdditionalComments}
          deletingCommentIndex={deletingCommentIndex}
          setDeletingCommentIndex={setDeletingCommentIndex}
          title={`Additional Comment ${deletingCommentIndex + 1}`}
          name="additional comment"
        />
      )}
    </>
  );
};

export default RegisterAdditionalComment;
